import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Map, Overlay } from "pigeon-maps";
import { Skeleton } from "antd";
import { homeStore } from "@/_zustand/home";
import { useMachine } from "@/_zustand/machine";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { useMaquinasPorComuna } from "@/hooks/useMaquinasPorComuna";
import {
  filtrarMaquinasPorEmpresa,
  descartarOutliers,
  calcularEncuadre,
  MAPA_CENTRO_DEFECTO,
  MAPA_MIN_ZOOM,
  MAPA_MAX_ZOOM,
  MAPA_MSG_SIN_MAQUINAS,
  MAPA_MSG_ERROR_CARGA,
} from "@/lib/mapaUbicaciones";

// Zoom inicial/por defecto, antes de cualquier encuadre calculado (mismo
// valor que el zoom fijo original de 14, conservado para no cambiar el look
// inicial del modal recién abierto).
const MAPA_ZOOM_INICIAL = 14;

// Componente unificado detrás de los dos modales antes idénticos byte a byte
// (ModalUbicacionesPorEmpresa.jsx / ModalUbicacionesPorOferta.jsx, hoy shims
// de re-export hacia este archivo — ver CLAUDE.md). Misma firma de props que
// antes: { show, setShow, listaComunas, empresaId }; `setListaComunas` llega
// desde OfertaDelDia.jsx pero no se usa acá (se tolera como prop extra).
export const ModalUbicacionesEmpresa = ({ show, setShow, listaComunas, empresaId }) => {
  const navigate = useNavigate();
  const { setComuna } = homeStore();
  const { setMachine } = useMachine();
  const { getCompanyRoute } = useCompanyFlow();

  const [showModalMap, setShowModalMap] = useState(false);
  const [selectValue, setSelectValue] = useState(0);
  const [center, setCenter] = useState(MAPA_CENTRO_DEFECTO);
  const [zoom, setZoom] = useState(MAPA_ZOOM_INICIAL);

  // `containerNode` (en vez de leer un useRef imperativamente dentro del
  // efecto) es lo que hace confiable el encuadre del criterio 13: un
  // callback ref dispara un re-render cuando el <div> del mapa realmente se
  // monta, así el efecto de fitKey puede depender de ese evento en vez de
  // asumir que el commit que cambia `estado`/`maquinasEmpresa` es siempre el
  // mismo commit que monta el contenedor (el <Skeleton> de antd desmonta/
  // remonta `children` por completo al salir de loading, no sólo lo oculta
  // con CSS). Antes, si el <div> aún no existía cuando corría el efecto, el
  // cálculo se saltaba en silencio y el mapa quedaba en el centro/zoom por
  // defecto sin ningún reintento (bug intermitente reportado por QA UI/UX).
  const [containerNode, setContainerNode] = useState(null);
  const fitKeyRef = useRef("");

  const { maquinas, estado, cargarComuna, reset } = useMaquinasPorComuna();

  // Sólo las máquinas de la empresa del banner/oferta influyen en centro,
  // zoom y pins (criterio 8) — el filtro ocurre ANTES del encuadre, no sólo
  // en el render de los pines (era exactamente el bug: el centro salía del
  // índice 0 de la lista sin filtrar).
  const maquinasEmpresa = useMemo(
    () => descartarOutliers(filtrarMaquinasPorEmpresa(maquinas, empresaId)),
    [maquinas, empresaId],
  );

  // El "fit key" sólo cambia cuando cambian comuna/empresa/el set de
  // máquinas -> el pan/zoom manual del usuario (onBoundsChanged) no se
  // revierte en cada re-render (criterio 16), pero cambiar de comuna sí
  // re-encuadra (criterio 14).
  const fitKey = `${empresaId}|${selectValue}|${maquinasEmpresa.map((m) => m.maquina_id).join(",")}`;

  useEffect(() => {
    if (fitKeyRef.current === fitKey) return;

    if (maquinasEmpresa.length === 0) {
      fitKeyRef.current = fitKey;
      setCenter(MAPA_CENTRO_DEFECTO);
      setZoom(MAPA_ZOOM_INICIAL);
      return;
    }

    if (!containerNode) {
      // Contenedor del mapa todavía no montado en este commit -- no
      // consumimos fitKeyRef, así el efecto reintenta en cuanto
      // `containerNode` cambie (ver callback ref del <div>) en vez de
      // quedar pegado al centro/zoom por defecto.
      return;
    }

    fitKeyRef.current = fitKey;
    const widthPx = containerNode.clientWidth || 700;
    const encuadre = calcularEncuadre(maquinasEmpresa, { widthPx, heightPx: 480 });
    if (encuadre) {
      setCenter(encuadre.center);
      setZoom(encuadre.zoom);
    }
  }, [fitKey, maquinasEmpresa, containerNode]);

  const handleSelect = (e) => {
    const value = e.target.value;
    setSelectValue(value);
    setComuna(value);

    if (value != 0) {
      setShowModalMap(true);
      const selectedOption = e.target.options[e.target.selectedIndex];
      const region = selectedOption.getAttribute("data-region");
      cargarComuna({ idRegion: region, idComuna: value });
    } else {
      setShowModalMap(false);
      reset();
    }
  };

  // Sólo estado local: reset() invalida cualquier petición en vuelo (last-wins
  // por reqId, ver useMaquinasPorComuna) para que una respuesta tardía no
  // reabra el mapa ni dispare un re-encuadre después de cerrar. Ya no se
  // llama a mutateAsync(0) (payload inválido -> region=undefined&comuna=undefined
  // -> el crash original) ni a removeQueries.
  const cerrarModal = () => {
    reset();
    setCenter(MAPA_CENTRO_DEFECTO);
    setZoom(MAPA_ZOOM_INICIAL);
    setShowModalMap(false);
    setShow(false);
    setComuna(0);
    setSelectValue(0);
  };

  const machineClick = (machine) => {
    navigate(getCompanyRoute(`store/${machine.maquina_id}`), { replace: false });
    setMachine(machine);
    cerrarModal();
  };

  const comunasValidas = Array.isArray(listaComunas) ? listaComunas : [];

  return (
    <Modal show={show} onHide={cerrarModal} size="lg">
      <Modal.Header>
        <Modal.Title>Ubicación de retiro</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label htmlFor="ubicaciones" className="visually-hidden">
          Seleccione una comuna
        </label>
        <select
          value={selectValue}
          onChange={handleSelect}
          id="ubicaciones"
          name="comunas"
          className="form-select"
        >
          <option value="0">Seleccione una comuna</option>
          {comunasValidas.map((item) =>
            (Array.isArray(item?.comunas) ? item.comunas : []).map((comuna) => (
              <option key={comuna.comuna_id} value={comuna.comuna_id} data-region={item.region_id}>
                {comuna.comuna_nombre}
              </option>
            )),
          )}
        </select>
        <div className="col-12 col-lg-12 rounded-1 p-3 bg-white d-grid gap-2 rounded-6" />

        {showModalMap && (
          <Skeleton loading={estado === "cargando"}>
            {estado === "error" ? (
              <div className="MB-3 text-center py-4">{MAPA_MSG_ERROR_CARGA}</div>
            ) : estado === "ok" && maquinasEmpresa.length === 0 ? (
              <div className="MB-3 text-center py-4">{MAPA_MSG_SIN_MAQUINAS}</div>
            ) : (
              <div className="MB-3 text-center" ref={setContainerNode}>
                <Map
                  height={480}
                  center={center}
                  zoom={zoom}
                  minZoom={MAPA_MIN_ZOOM}
                  maxZoom={MAPA_MAX_ZOOM}
                  onBoundsChanged={({ center: newCenter, zoom: newZoom }) => {
                    setCenter(newCenter);
                    setZoom(newZoom);
                  }}
                >
                  {maquinasEmpresa.map((item) => (
                    <Overlay
                      key={item.maquina_id}
                      anchor={[item.maquina_latitud, item.maquina_longitud]}
                      offset={[12, 38]}
                    >
                      <img
                        src={item.maquinamodelo_icono}
                        width={45}
                        alt="icon"
                        onClick={() => machineClick(item)}
                        style={{ cursor: "pointer" }}
                      />
                    </Overlay>
                  ))}
                </Map>
              </div>
            )}
          </Skeleton>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={cerrarModal}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
